/*
 *  -----------------------------------------------------------------------
 *    <copyright file="users-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import moment from 'moment';

import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useTranslation, useGridCells } from 'hooks';
// eslint-disable-next-line max-len
import { OrganisationAndStudyMemberFields_Organisation_members_OrganisationMember as OrganisationAndStudyMemberFields_members } from 'queries/generated/fragments.generated';
import { OrganisationMemberStatus } from 'types/graphql-global-types';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import { RestructuredUpdatedCreatedBy } from 'utils/filter';
import { IActionsMenuParams } from 'views/components/core/actions-menu/actions-menu.interface';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { ExportExcelComponentInputColumn } from 'views/components/core/export-excel';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

export interface OrganisationUserGridItem
    extends OrganisationAndStudyMemberFields_members,
        RestructuredUpdatedCreatedBy {
    isEnabled: boolean | null;
    lastLoginTime: number | null;
    role: string;
    studyCount: number;
    smStudyCount: number;
    studyList: string;
    _status?: string;
    _statusOrder?: number;
}

interface UsersGridProps {
    items: OrganisationUserGridItem[];
    actions: (item: OrganisationUserGridItem) => IActionsMenuParams[];
    organisationName: String;
}

const UsersGrid = ({ items, actions, organisationName }: UsersGridProps) => {
    const { t } = useTranslation();
    const { YesNoCell, DateCell, StandardCell, DateTimeCell } = useGridCells();
    const [data, setData] = useState([] as OrganisationUserGridItem[]);

    useEffect(() => {
        let array = [...items].map((item: any) => {
            const status =
                item.status === OrganisationMemberStatus.VERIFYING || item.status === OrganisationMemberStatus.JOINED
                    ? 'Verified'
                    : item.status;
            return {
                ...item,
                _status: status,
                _statusOrder: status === 'Verified' ? 1 : status === 'Invited' ? 2 : 3,
            } as OrganisationUserGridItem;
        });
        const newData = restructuredUpdatedCreatedBy(array);
        array = orderBy(newData, [
            { field: '_statusOrder', dir: 'asc' },
            { field: 'lastName', dir: 'asc' },
        ]);
        setData(array);
    }, [items]);

    const columns = [
        {
            field: 'studyCount',
            id: 'user-study-count',
            label: t('uam:study'),
            minWidth: 200,
            lockedByDefault: true,
            filter: 'numeric',
        },
        {
            field: 'smStudyCount',
            id: 'user-sm-study-count',
            label: t('uam:smStudy'),
            minWidth: 150,
            lockedByDefault: true,
            filter: 'numeric',
        },
        {
            field: 'lastName',
            id: 'user-last-name',
            label: t('lastName'),
            minWidth: 180,
            lockedByDefault: true,
        },
        {
            field: 'firstName',
            id: 'user-first-name',
            label: t('firstName'),
            minWidth: 180,
        },
        {
            field: 'email',
            id: 'user-email',
            label: t('email'),
            minWidth: 180,
        },
        {
            field: 'role',
            id: 'user-role',
            label: t('role'),
            minWidth: 140,
        },
        {
            field: '_status',
            id: 'user-status',
            label: t('ra:status'),
            minWidth: 140,
            cells: { data: StandardCell(true) },
        },
        {
            label: t('active'),
            id: 'uer-active',
            field: 'isActive',
            minWidth: 130,
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('uam:unlocked'),
            id: 'uer-unlocked',
            field: 'isEnabled',
            minWidth: 150,
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('uam:userAdministrator'),
            id: 'user-admin',
            field: 'isAdmin',
            minWidth: 220,
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('uam:last-login'),
            id: 'uer-lastLogin',
            field: 'lastLoginTime',
            minWidth: 200,
            cells: { data: DateTimeCell('DD-MMM-YYYY HH:mm:ss UTC') },
            filter: 'date',
        },
        {
            label: t('uam:auditLogAccess'),
            id: 'user-audit-admin',
            field: 'isAuditAdmin',
            minWidth: 220,
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('uam:reportAdmin'),
            id: 'user-report-admin',
            field: 'isReportAdmin',
            minWidth: 230,
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('uam:ObsAndLogsAdmin'),
            id: 'user-obs-and-logs-admin',
            field: 'isObsAndLogsAdmin',
            minWidth: 230,
            cells: { data: YesNoCell },
            filterUi: GridColumnMenuActiveFilter,
        },
        {
            label: t('ra:createdBy'),
            field: '_createdByString',
            id: 'user-createdby',
            minWidth: 200,
        },
        {
            label: t('ra:createdDate'),
            field: 'created',
            id: 'user-created',
            minWidth: 200,
            cells: { data: DateCell },
            filter: 'date',
        },
        {
            label: t('ra:updatedBy'),
            field: '_updatedByString',
            id: 'user-updatedby',
            minWidth: 200,
        },
        {
            label: t('ra:updatedDate'),
            field: 'updated',
            id: 'user-updated',
            minWidth: 200,
            cells: { data: DateCell },
            filter: 'date',
        },
    ];
    const getFileName = () => {
        const prefix = `${organisationName}_`;
        return `${prefix}${t('uam:exportFileName')}_${formatDateUTC_DEPRECATED(moment(), true, 'YYYYMMDD')}`;
    };

    const mapColumns = (inputColumns: ExportExcelComponentInputColumn[]) =>
        inputColumns.map((column: ExportExcelComponentInputColumn) => {
            if (column.field === 'studyCount')
                return {
                    field: 'studyList',
                    label: t('uam:study'),
                    minWidth: 140,
                    show: column.show,
                } as ExportExcelComponentInputColumn;

            return column;
        });

    return (
        <ExpandableGrid
            customKey="data-users-grid"
            items={data}
            showPager
            groupable={false}
            rowActions={actions}
            scrollable="scrollable"
            reorderable
            pageable={false}
            resizable
            addScroll
            options={{
                allowExcelExport: true,
                allowExpandCollapseAll: false,
                useFilterChips: false,
            }}
            excelExportOptions={{
                fileName: getFileName(),
                name: getFileName(),
                mapColumns,
            }}
            detail={null}
            columns={columns}
        />
    );
};

export default UsersGrid;
